/**
 * BlogsTab: Blog post list + editor rendered inside ContentSwitcherPanel.
 * Two views: list (all posts with search) and edit (metadata + block editor for selected post).
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ArrowLeft, ChevronDown, Search, X } from "lucide-react"
import { toast } from "sonner"
import debounce from "lodash.debounce"
import {
  useBlogPosts,
  useBlogPost,
  useCreateBlogPost,
  useUpdateBlogPost,
  getStatus,
} from "@/hooks/useBlogPosts"
import {
  useBlogCategories,
  useCreateBlogCategory,
} from "@/hooks/useBlogCategories"
import type { BlockType } from "./blocks/types"
import type { PageSection } from "@/templates/types"
import { generateSlug } from "@/lib/slugUtils"
import type { Json } from "@/types/database"
import type { BlogLiveData } from "./BlogPostCanvas"
import { BUILDER_FONT } from "./ui/FloatingPanel"
import { GROUP_LABEL, PANEL_INPUT, PANEL_TEXTAREA } from "./ui/GroupLabel"

// Labels sit 1px tighter than the kit default here (pixel parity)
const LABEL: React.CSSProperties = { ...GROUP_LABEL, marginBottom: 5 }

const INPUT = PANEL_INPUT

const TEXTAREA = PANEL_TEXTAREA

function statusBadge(status: "draft" | "published" | "scheduled") {
  const colors = {
    draft: { bg: "rgba(255,255,255,0.1)", text: "rgba(255,255,255,0.5)" },
    published: { bg: "rgba(34,197,94,0.15)", text: "#22c55e" },
    scheduled: { bg: "rgba(245,158,11,0.15)", text: "#f59e0b" },
  }
  const { bg, text } = colors[status]
  return (
    <span
      style={{
        background: bg,
        color: text,
        fontSize: 9,
        fontWeight: 600,
        padding: "2px 7px",
        borderRadius: 20,
        textTransform: "uppercase" as const,
        letterSpacing: "0.06em",
        flexShrink: 0,
      }}
    >
      {status}
    </span>
  )
}

// ─── CategoryCombobox ────────────────────────────────────────────────────────

interface CategoryComboboxProps {
  orgId: string
  value: string | null
  onChange: (categoryId: string | null, categoryName: string | null, categoryColor: string | null) => void
}

function CategoryCombobox({ orgId, value, onChange }: CategoryComboboxProps) {
  const { categories, refresh } = useBlogCategories(orgId)
  const { createBlogCategory } = useCreateBlogCategory(orgId)
  const [search, setSearch] = useState("")
  const [open, setOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selected = categories.find((c) => c.id === value) ?? null
  const displayText = selected?.name ?? "No category"

  const filtered = search
    ? categories.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase())
      )
    : categories

  const exactMatch = categories.some(
    (c) => c.name.toLowerCase() === search.toLowerCase()
  )

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      const target = e.target as Node
      if (
        !triggerRef.current?.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setOpen(false)
        setSearch("")
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [open])

  async function handleCreate() {
    if (!search.trim() || creating) return
    setCreating(true)
    const cat = await createBlogCategory({ name: search.trim() })
    setCreating(false)
    if (cat) {
      await refresh()
      onChange(cat.id, cat.name, cat.color)
      setOpen(false)
      setSearch("")
    }
  }

  function handleToggle() {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    }
    setOpen((v) => !v)
    setSearch("")
  }

  const dropdownEl = open
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "fixed",
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            background: "rgba(18,18,24,0.99)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 8,
            boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
            zIndex: 9999,
            maxHeight: 220,
            overflowY: "auto" as const,
            // createPortal escapes .builder-root — the font must be set here
            fontFamily: BUILDER_FONT,
          }}
        >
          {/* Search input */}
          <div
            style={{
              padding: "8px 10px 4px",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Search size={11} color="rgba(255,255,255,0.3)" />
            <input
              autoFocus
              type="text"
              placeholder="Search or create…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !exactMatch && search.trim()) {
                  void handleCreate()
                }
              }}
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                color: "#fff",
                fontSize: 12,
              }}
            />
          </div>

          {/* No category option */}
          <button
            type="button"
            onClick={() => {
              onChange(null, null, null)
              setOpen(false)
              setSearch("")
            }}
            style={{
              width: "100%",
              padding: "7px 10px",
              background: value === null ? "rgba(255,255,255,0.06)" : "transparent",
              border: "none",
              color: "rgba(255,255,255,0.4)",
              fontSize: 12,
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            No category
          </button>

          {/* Create option */}
          {search.trim() && !exactMatch && (
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={creating}
              style={{
                width: "100%",
                padding: "7px 10px",
                background: "rgba(255,255,255,0.04)",
                border: "none",
                borderTop: "1px solid rgba(255,255,255,0.06)",
                color: "rgba(255,255,255,0.7)",
                fontSize: 12,
                textAlign: "left",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span style={{ color: "rgba(255,255,255,0.35)" }}>＋</span>
              {creating ? "Creating…" : `Create "${search.trim()}"`}
            </button>
          )}

          {/* Category list */}
          {filtered.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => {
                onChange(cat.id, cat.name, cat.color)
                setOpen(false)
                setSearch("")
              }}
              style={{
                width: "100%",
                padding: "7px 10px",
                background: cat.id === value ? "rgba(255,255,255,0.06)" : "transparent",
                border: "none",
                color: cat.id === value ? "#fff" : "rgba(255,255,255,0.6)",
                fontSize: 12,
                textAlign: "left",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {cat.color && (
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: cat.color,
                    flexShrink: 0,
                  }}
                />
              )}
              {cat.name}
            </button>
          ))}

          {filtered.length === 0 && !search.trim() && (
            <div
              style={{
                padding: "12px 10px",
                color: "rgba(255,255,255,0.3)",
                fontSize: 12,
                textAlign: "center",
              }}
            >
              No categories yet
            </div>
          )}
        </div>,
        document.body
      )
    : null

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        style={{
          ...INPUT,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          padding: "7px 10px",
        }}
      >
        <span style={{ color: selected ? "#fff" : "rgba(255,255,255,0.35)" }}>
          {displayText}
        </span>
        <ChevronDown size={12} color="rgba(255,255,255,0.3)" />
      </button>
      {dropdownEl}
    </div>
  )
}

// ─── Helper: blocks ↔ content_sections ──────────────────────────────────────

function contentSectionsToBlocks(contentSections: Json, orgId: string): PageSection[] {
  const arr = Array.isArray(contentSections) ? contentSections : []
  return (arr as Record<string, unknown>[]).map((item, i) => ({
    id: String(item.id ?? crypto.randomUUID()),
    section_type: String(item.section_type ?? "paragraph"),
    order_index: typeof item.order_index === "number" ? item.order_index : i,
    content: (item.content as Record<string, unknown>) ?? null,
    page_id: "",
    org_id: orgId,
    is_visible: true,
  }))
}

export function blocksToContentSections(blocks: PageSection[]): Json {
  return blocks
    .sort((a, b) => a.order_index - b.order_index)
    .map((b) => ({
      id: b.id,
      section_type: b.section_type,
      order_index: b.order_index,
      content: (b.content ?? {}) as Record<string, unknown>,
    })) as Json
}

// ─── BlogsTab ────────────────────────────────────────────────────────────────

export interface BlogsTabProps {
  orgId: string
  activeBlogPostId: string | null
  onSelectBlogPost: (postId: string | null) => void
  onBlogLiveUpdate: (data: BlogLiveData) => void
  onAddBlock?: (type: BlockType) => void
}

export function BlogsTab({
  orgId,
  activeBlogPostId,
  onSelectBlogPost,
  onBlogLiveUpdate,
  onAddBlock,
}: BlogsTabProps) {
  const { posts, loading: postsLoading, refresh: refreshPosts } = useBlogPosts(orgId)
  const { createBlogPost, loading: creating } = useCreateBlogPost(orgId)
  const { updateBlogPost } = useUpdateBlogPost(orgId)
  const [search, setSearch] = useState("")
  const [showEditor, setShowEditor] = useState(false)

  const filteredPosts = search
    ? posts.filter((p) => p.title.toLowerCase().includes(search.toLowerCase()))
    : posts

  async function handleNewPost() {
    try {
      const slug = `new-post-${Date.now()}`
      const post = await createBlogPost({ title: "New post", slug })
      if (!post) {
        toast.error("Failed to create post")
        return
      }
      const heroBlock: PageSection = {
        id: crypto.randomUUID(),
        section_type: "hero",
        order_index: 0,
        content: { headline: post.title, subheadline: "", cta_text: "" },
        page_id: "",
        org_id: orgId,
        is_visible: true,
      }
      const contentBlock: PageSection = {
        id: crypto.randomUUID(),
        section_type: "heading",
        order_index: 1,
        content: { text: "", level: 2 },
        page_id: "",
        org_id: orgId,
        is_visible: true,
      }
      const authorBlock: PageSection = {
        id: crypto.randomUUID(),
        section_type: "author_bio",
        order_index: 2,
        content: {
          heading: "About the Author",
          authors: [{ name: "", role: "", bio: "", social_links: [] }],
        },
        page_id: "",
        org_id: orgId,
        is_visible: true,
      }
      await updateBlogPost(post.id, {
        content_sections: blocksToContentSections([heroBlock, contentBlock, authorBlock]),
      })
      refreshPosts()
      onSelectBlogPost(post.id)
      setShowEditor(true)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create post")
    }
  }

  if (showEditor && activeBlogPostId) {
    return (
      <BlogPostEditor
        orgId={orgId}
        postId={activeBlogPostId}
        onBack={() => setShowEditor(false)}
        onBlogLiveUpdate={onBlogLiveUpdate}
        onRefreshList={refreshPosts}
        onAddBlock={onAddBlock ?? (() => {})}
      />
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Search */}
      <div style={{ padding: "12px 16px" }}>
        <div style={{ position: "relative" }}>
          <Search
            size={11}
            color="rgba(255,255,255,0.3)"
            style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)" }}
          />
          <input
            type="text"
            placeholder="Search posts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...INPUT, paddingLeft: 26 }}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 6,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                padding: 2,
                cursor: "pointer",
                color: "rgba(255,255,255,0.3)",
                display: "flex",
              }}
            >
              <X size={11} />
            </button>
          )}
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "0 0 4px" }} />

      {/* Post list */}
      <div style={{ padding: "2px 0" }}>
        {postsLoading ? (
          <div
            style={{
              padding: "20px 16px",
              color: "rgba(255,255,255,0.3)",
              fontSize: 12,
              textAlign: "center",
            }}
          >
            Loading…
          </div>
        ) : filteredPosts.length === 0 ? (
          <div
            style={{
              padding: "20px 16px",
              color: "rgba(255,255,255,0.3)",
              fontSize: 12,
              textAlign: "center",
            }}
          >
            {search ? "No posts match" : "No posts yet"}
          </div>
        ) : (
          filteredPosts.map((post) => {
            const status = getStatus(post.is_published, post.published_at)
            return (
              <button
                key={post.id}
                type="button"
                onClick={() => { onSelectBlogPost(post.id); setShowEditor(true) }}
                style={{
                  width: "100%",
                  padding: "8px 16px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  textAlign: "left",
                }}
                className="blog-post-item"
              >
                <span style={{ fontSize: 14, flex: "none" }}>📝</span>
                <span
                  style={{
                    flex: 1,
                    fontSize: 12,
                    color: "rgba(255,255,255,0.75)",
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap" as const,
                    minWidth: 0,
                  }}
                >
                  {post.title}
                </span>
                {statusBadge(status)}
              </button>
            )
          })
        )}
      </div>

      {/* Divider + New post */}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", margin: "4px 0 0" }} />
      <button
        type="button"
        onClick={() => void handleNewPost()}
        disabled={creating}
        style={{
          padding: "8px 16px",
          fontSize: 12,
          color: "rgba(255,255,255,0.35)",
          background: "transparent",
          border: "none",
          width: "100%",
          textAlign: "left",
          cursor: creating ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          gap: 8,
          opacity: creating ? 0.5 : 1,
        }}
      >
        <span>＋</span>
        <span>{creating ? "Creating…" : "New post"}</span>
      </button>

      <style>{`
        .blog-post-item:hover { background: rgba(255,255,255,0.04) !important; }
      `}</style>
    </div>
  )
}

// ─── BlogPostEditor ──────────────────────────────────────────────────────────

interface BlogPostEditorProps {
  orgId: string
  postId: string
  onBack: () => void
  onBlogLiveUpdate: (data: BlogLiveData) => void
  onRefreshList: () => void
  onAddBlock: (type: BlockType) => void
}

type PublishMode = "draft" | "publish" | "schedule"

function BlogPostEditor({
  orgId,
  postId,
  onBack,
  onBlogLiveUpdate,
  onRefreshList,
  onAddBlock: _onAddBlock,
}: BlogPostEditorProps) {
  const { post, loading } = useBlogPost(orgId, postId)
  const { updateBlogPost } = useUpdateBlogPost(orgId)
  const { categories } = useBlogCategories(orgId)

  const [title, setTitle] = useState("")
  const [slug, setSlug] = useState("")
  const [slugTouched, setSlugTouched] = useState(false)
  const [excerpt, setExcerpt] = useState("")
  const [coverImage, setCoverImage] = useState("")
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [categoryName, setCategoryName] = useState<string | null>(null)
  const [categoryColor, setCategoryColor] = useState<string | null>(null)
  // NOS-249 per-post SEO overrides — public pages fall back title/excerpt/cover,
  // then the generated branded OG default (src/lib/seo/ogImage.ts)
  const [seoTitle, setSeoTitle] = useState("")
  const [seoDescription, setSeoDescription] = useState("")
  const [ogImage, setOgImage] = useState("")
  const [publishMode, setPublishMode] = useState<PublishMode>("draft")
  const [scheduledAt, setScheduledAt] = useState("")
  const [blocks, setBlocks] = useState<PageSection[]>([])
  const [initialized, setInitialized] = useState(false)

  // Initialize from DB post on first load
  useEffect(() => {
    if (!post || initialized) return
    setTitle(post.title)
    setSlug(post.slug)
    setExcerpt(post.excerpt ?? "")
    setCoverImage(post.cover_image ?? "")
    setCategoryId(post.category_id)
    setSeoTitle(post.seo_title ?? "")
    setSeoDescription(post.seo_description ?? "")
    setOgImage(post.og_image ?? "")

    const cat = post.category_id
      ? categories.find((c) => c.id === post.category_id) ?? null
      : null
    setCategoryName(cat?.name ?? null)
    setCategoryColor(cat?.color ?? null)

    const status = getStatus(post.is_published, post.published_at)
    const pm: PublishMode = status === "draft" ? "draft" : status === "scheduled" ? "schedule" : "publish"
    const sa = (status === "scheduled" || status === "published") && post.published_at
      ? post.published_at.slice(0, 16)
      : ""
    if (status === "draft") setPublishMode("draft")
    else if (status === "scheduled") {
      setPublishMode("schedule")
      setScheduledAt(post.published_at ? post.published_at.slice(0, 16) : "")
    } else {
      setPublishMode("publish")
      setScheduledAt(post.published_at ? post.published_at.slice(0, 16) : "")
    }

    const loadedBlocks = contentSectionsToBlocks(post.content_sections ?? [], orgId)
    setBlocks(loadedBlocks)
    setInitialized(true)

    // Immediately emit to canvas so it switches without waiting for user input
    emitLive({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt ?? "",
      coverImage: post.cover_image ?? "",
      categoryId: post.category_id,
      categoryName: cat?.name ?? null,
      categoryColor: cat?.color ?? null,
      publishMode: pm,
      scheduledAt: sa,
      blocks: loadedBlocks,
    })
  }, [post, categories, orgId, initialized]) // eslint-disable-line react-hooks/exhaustive-deps

  // Emit live data to canvas
  function emitLive(overrides: Partial<{
    title: string; slug: string; excerpt: string; coverImage: string
    categoryId: string | null; categoryName: string | null; categoryColor: string | null
    publishMode: PublishMode; scheduledAt: string; blocks: PageSection[]
  }> = {}) {
    const t = overrides.title ?? title
    const sl = overrides.slug ?? slug
    const ex = overrides.excerpt ?? excerpt
    const ci = overrides.coverImage ?? coverImage
    const cId = overrides.categoryId !== undefined ? overrides.categoryId : categoryId
    const cName = overrides.categoryName !== undefined ? overrides.categoryName : categoryName
    const cColor = overrides.categoryColor !== undefined ? overrides.categoryColor : categoryColor
    const pm = overrides.publishMode ?? publishMode
    const sa = overrides.scheduledAt ?? scheduledAt
    const bl = overrides.blocks ?? blocks

    const { is_published, published_at } = (() => {
      if (pm === "draft") return { is_published: false, published_at: null }
      if (pm === "publish") return { is_published: true, published_at: new Date().toISOString() }
      return { is_published: true, published_at: sa ? new Date(sa).toISOString() : null }
    })()

    onBlogLiveUpdate({
      title: t,
      slug: sl,
      excerpt: ex || null,
      cover_image: ci || null,
      category_id: cId,
      category_name: cName,
      category_color: cColor,
      is_published,
      published_at,
      updated_at: post?.updated_at ?? null,
      blocks: bl,
    })
  }

  // Debounced save
  const debouncedSave = useMemo(
    () =>
      debounce(async (updates: Record<string, unknown>) => {
        await updateBlogPost(postId, updates)
        onRefreshList()
      }, 800),
    [postId, updateBlogPost, onRefreshList]
  )

  function handleTitleChange(val: string) {
    setTitle(val)
    const newSlug = slugTouched ? slug : generateSlug(val)
    if (!slugTouched) setSlug(newSlug)
    emitLive({ title: val, slug: newSlug })
    debouncedSave({ title: val, slug: newSlug })
  }

  function handleSlugChange(val: string) {
    setSlug(val)
    setSlugTouched(true)
    emitLive({ slug: val })
    debouncedSave({ slug: val })
  }

  function handleExcerptChange(val: string) {
    setExcerpt(val)
    emitLive({ excerpt: val })
    debouncedSave({ excerpt: val || null })
  }

  function handleCoverImageChange(val: string) {
    setCoverImage(val)
    emitLive({ coverImage: val })
    debouncedSave({ cover_image: val || null })
  }

  function handleCategoryChange(
    newCategoryId: string | null,
    newCategoryName: string | null,
    newCategoryColor: string | null
  ) {
    setCategoryId(newCategoryId)
    setCategoryName(newCategoryName)
    setCategoryColor(newCategoryColor)
    emitLive({ categoryId: newCategoryId, categoryName: newCategoryName, categoryColor: newCategoryColor })
    debouncedSave({ category_id: newCategoryId })
  }

  // SEO fields are meta-only — nothing to emit to the canvas
  function handleSeoTitleChange(val: string) {
    setSeoTitle(val)
    debouncedSave({ seo_title: val.trim() || null })
  }

  function handleSeoDescriptionChange(val: string) {
    setSeoDescription(val)
    debouncedSave({ seo_description: val.trim() || null })
  }

  function handleOgImageChange(val: string) {
    setOgImage(val)
    debouncedSave({ og_image: val.trim() || null })
  }

  const currentStatus = (() => {
    if (publishMode === "draft") return "draft" as const
    if (publishMode === "publish") return "published" as const
    return "scheduled" as const
  })()

  if (loading && !initialized) {
    return (
      <div
        style={{
          padding: 24,
          color: "rgba(255,255,255,0.3)",
          fontSize: 12,
          textAlign: "center",
        }}
      >
        Loading…
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Back + status header */}
      <div
        style={{
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          gap: 10,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 2,
            color: "rgba(255,255,255,0.5)",
            display: "flex",
            alignItems: "center",
          }}
        >
          <ArrowLeft size={14} />
        </button>
        <span
          style={{
            fontSize: 12,
            fontWeight: 500,
            color: "rgba(255,255,255,0.7)",
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap" as const,
          }}
        >
          {title || "Untitled"}
        </span>
        {statusBadge(currentStatus)}
      </div>

      {/* Form fields */}
      <div
        style={{
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
          overflowY: "auto" as const,
        }}
      >
        {/* Title */}
        <div>
          <label style={LABEL}>Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Post title"
            style={INPUT}
          />
        </div>

        {/* Slug */}
        <div>
          <label style={LABEL}>URL slug</label>
          <input
            type="text"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            placeholder="post-url-slug"
            style={INPUT}
          />
        </div>

        {/* Excerpt */}
        <div>
          <label style={LABEL}>Excerpt</label>
          <textarea
            value={excerpt}
            onChange={(e) => handleExcerptChange(e.target.value)}
            placeholder="Short summary…"
            rows={3}
            style={TEXTAREA}
          />
        </div>

        {/* Cover image */}
        <div>
          <label style={LABEL}>Cover image URL</label>
          <input
            type="text"
            value={coverImage}
            onChange={(e) => handleCoverImageChange(e.target.value)}
            placeholder="https://…"
            style={INPUT}
          />
        </div>

        {/* Category */}
        <div>
          <label style={LABEL}>Category</label>
          <CategoryCombobox
            orgId={orgId}
            value={categoryId}
            onChange={handleCategoryChange}
          />
        </div>

        {/* SEO */}
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            paddingTop: 14,
          }}
        >
          <label style={LABEL} htmlFor="blog-seo-title">SEO title</label>
          <input
            id="blog-seo-title"
            type="text"
            value={seoTitle}
            onChange={(e) => handleSeoTitleChange(e.target.value)}
            placeholder="Override for search results"
            style={INPUT}
          />
        </div>

        <div>
          <label style={LABEL} htmlFor="blog-seo-description">SEO description</label>
          <textarea
            id="blog-seo-description"
            value={seoDescription}
            onChange={(e) => handleSeoDescriptionChange(e.target.value)}
            placeholder="Defaults to the excerpt"
            rows={2}
            style={TEXTAREA}
          />
        </div>

        <div>
          <label style={LABEL} htmlFor="blog-og-image">Social share image URL</label>
          <input
            id="blog-og-image"
            type="text"
            value={ogImage}
            onChange={(e) => handleOgImageChange(e.target.value)}
            placeholder="Defaults to the cover image"
            style={INPUT}
          />
        </div>

      </div>

    </div>
  )
}
